import React, { useEffect, useState, useRef } from "react";
import {
  getServiceVolumeHealth,
  searchAllUsers,
  searchUser,
} from "../../../services/apiService";
import { showErrorToast } from "../../../utils/toastUtils";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import { fetchVendorServiceUsage } from "../../../services/customerService";

function ServiceVolumeHealth() {
  // Input states
  const [userId, setUserId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [serviceName, setServiceName] = useState("");

  // Debounce & Dropdown states
  const [debouncedUserId, setDebouncedUserId] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [backupAllUsers, setBackupAllUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const userDropdownRef = useRef(null);

  const [serviceList, setServiceList] = useState([]);
  const [backupServiceList, setBackupServiceList] = useState([]);
  const [showServiceList, setShowServiceList] = useState(false);

  // Data states
  const [responseData, setResponseData] = useState(null);

  // UI Tab & Pagination states
  const [activeMonth, setActiveMonth] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [reportLoading, setReportLoading] = useState(false);

  // --- API Calls ---

  const handleSearchUser = async () => {
    try {
      const res = await searchAllUsers();
      if (res?.status?.code === 200) {
        setAllUsers(res?.data || []);
        setBackupAllUsers(res?.data || []);
        setShowUserList(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchServiceHealth = async () => {
    if (!userId) {
      return showErrorToast("Please enter User ID");
    }

    setReportLoading(true);

    const payload = { user_id: userId };
    if (fromDate) payload.from_date = fromDate;
    if (toDate) payload.to_date = toDate;
    if (serviceName) payload.service_name = serviceName;

    try {
      const res = await getServiceVolumeHealth(payload);

      if (res) {
        console.log("res of service volume", res);
      }
      if (res?.status?.code === 200) {
        setResponseData(res?.data);
        if (res?.data?.months?.length > 0) {
          setActiveMonth(res.data.months[res?.data?.months.length - 1].month);
        }
        setCurrentPage(1); // Reset pagination on new fetch
      } else {
        showErrorToast(
          res?.message ||
            res?.data?.message ||
            res?.data?.data?.message ||
            res?.data.error?.message,
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setReportLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedUserId(userId);
    }, 500);
    return () => clearTimeout(timeout);
  }, [userId]);

  useEffect(() => {
    handleSearchUser();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await fetchVendorServiceUsage();
        if (response?.data?.services?.data) {
          setBackupServiceList(response?.data?.services?.data);
          setServiceList(response?.data?.services?.data);
        }
      } catch (error) {
        showErrorToast("Failed to load services");
      }
    };
    loadServices();
  }, []);

  useEffect(() => {
    if (serviceName === "") setServiceList(backupServiceList);
    const target = serviceName?.toLowerCase()?.trim();
    const match = backupServiceList?.filter((item) =>
      item?.service_name?.toLowerCase().includes(target),
    );
    setServiceList(match);
  }, [serviceName, backupServiceList]);

  // filter user ids
  useEffect(() => {
    if (userId === "") setAllUsers(backupAllUsers);
    const target = userId?.toLowerCase()?.trim();
    const match = backupAllUsers?.filter((item) =>
      item?.toLowerCase().includes(target),
    );
    setAllUsers(match);
  }, [userId, backupAllUsers]);

  // Click outside listener for User Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Helpers ---

  const activeMonthData = responseData?.months?.find(
    (m) => m.month === activeMonth,
  );

  // Pagination logic for bottom table
  const totalGlobalServices = responseData?.services?.length || 0;
  const totalPages = Math.ceil(totalGlobalServices / itemsPerPage);
  const currentTableData = responseData?.services?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="p-6  mx-auto space-y-8 bg-gray-50 min-h-screen">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Service Volume & Health Report
        </h1>

        {/* --- FORM FILTERS --- */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          {/* User ID Input with Dropdown */}
          <div className="relative" ref={userDropdownRef}>
            <label
              htmlFor="user_id"
              className="block text-sm font-semibold mb-2 text-gray-700"
            >
              User ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              autoComplete="off"
              id="user_id"
              placeholder="Search User ID"
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setShowUserList(true);
              }}
              onFocus={() => setShowUserList(true)}
            />
            {allUsers?.length > 0 && showUserList && (
              <div className="absolute top-full left-0 mt-2 w-full z-30 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
                  <span className="text-xs font-semibold text-gray-500">
                    Select User
                  </span>
                  <button
                    onClick={() => setShowUserList(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <ul className="max-h-48 overflow-y-auto">
                  {allUsers.map((u, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        setUserId(u || u?.id);
                        setShowUserList(false);
                      }}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
                    >
                      {u || u?.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Service Name Input */}
          <div className="relative">
            <label
              htmlFor="service_name"
              className="block text-sm font-semibold mb-2 text-gray-700"
            >
              Service Name
            </label>
            <input
              type="search"
              autoComplete="off"
              id="service_name"
              placeholder="All Services"
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              onFocus={() => setShowServiceList(true)}
            />
            {serviceList?.length > 0 && showServiceList && (
              <div className="absolute top-full left-0 mt-2 w-72 z-30 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
                  <span className="text-xs font-semibold text-gray-500">
                    Select Service
                  </span>
                  <button
                    onClick={() => setShowServiceList(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {serviceList.map((item, idx) => (
                    <button
                      key={item?.id || idx}
                      onClick={() => {
                        setServiceName(item?.service_name);
                        setShowServiceList(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer truncate"
                    >
                      {item?.service_name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* From Date */}
          <div>
            <label
              htmlFor="from_date"
              className="block text-sm font-semibold mb-2 text-gray-700"
            >
              From Date
            </label>
            <input
              type="date"
              id="from_date"
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          {/* To Date */}
          <div>
            <label
              htmlFor="to_date"
              className="block text-sm font-semibold mb-2 text-gray-700"
            >
              To Date
            </label>
            <input
              type="date"
              id="to_date"
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            disabled={reportLoading}
            onClick={fetchServiceHealth}
            className="bg-blue-600 flex justify-center items-center gap-2 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg shadow transition duration-200"
          >
            {reportLoading ? (
              <>
                <Loader2 className="animate-spin" />
                <span className="text-sm text-gray-300"> Please Wait...</span>
              </>
            ) : (
              "Get Report"
            )}
          </button>
        </div>
      </div>

      {/* --- REPORT RENDERING --- */}
      {responseData && (
        <div className="space-y-8">
          {/* --- MONTH TABS & MONTHLY DATA --- */}
          {responseData.months?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Tab Header */}
              <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
                {responseData.months.map((m, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveMonth(m.month)}
                    className={`px-6 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
                      activeMonth === m.month
                        ? "border-blue-600 text-blue-600 bg-white"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {m.month}
                  </button>
                ))}
              </div>

              {/* Monthly Content */}
              {activeMonthData && (
                <div className="p-6">
                  {/* Month Stats Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase font-semibold">
                        Total Requests
                      </p>
                      <p className="text-xl font-bold text-blue-700">
                        {activeMonthData.total_requests}
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase font-semibold">
                        Success
                      </p>
                      <p className="text-xl font-bold text-green-700">
                        {activeMonthData.success_requests}
                      </p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase font-semibold">
                        Failed
                      </p>
                      <p className="text-xl font-bold text-red-700">
                        {activeMonthData.failed_requests}
                      </p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase font-semibold">
                        Vendor Reqs
                      </p>
                      <p className="text-xl font-bold text-yellow-700">
                        {activeMonthData.vendor_requests}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase font-semibold">
                        Cache Reqs
                      </p>
                      <p className="text-xl font-bold text-purple-700">
                        {activeMonthData.cache_requests}
                      </p>
                    </div>
                  </div>

                  {/* Month Specific Services Table */}
                  <div className="overflow-x-auto">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">
                      {activeMonthData.month} Services Breakdown
                    </h3>
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                          <th className="p-3 font-semibold rounded-tl-lg">
                            Service Info
                          </th>
                          <th className="p-3 font-semibold">Vendor</th>
                          <th className="p-3 font-semibold text-right">
                            Total
                          </th>
                          <th className="p-3 font-semibold text-right">
                            Success
                          </th>
                          <th className="p-3 font-semibold text-right">
                            Failed
                          </th>
                          <th className="p-3 font-semibold text-right">
                            Cache
                          </th>
                          <th className="p-3 font-semibold text-right">
                            Vendor (Req / Succ)
                          </th>
                          <th className="p-3 font-semibold text-center">
                            Health Trend
                          </th>
                          <th className="p-3 font-semibold text-right rounded-tr-lg">
                            Rate (%)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {activeMonthData.services?.map((svc, idx) => (
                          <tr
                            key={svc.service_id || idx}
                            className="hover:bg-gray-50 transition"
                          >
                            {/* Service Name, ID & Code */}
                            <td className="p-3">
                              <p className="text-sm font-semibold text-gray-800">
                                {svc.service_name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Code:{" "}
                                <span className="font-mono">
                                  {svc.service_code}
                                </span>{" "}
                                | ID: {svc.service_id}
                              </p>
                            </td>

                            {/* Vendor Name */}
                            <td className="p-3 text-sm text-gray-600">
                              {svc.vendor_name}
                            </td>

                            {/* Request Metrics */}
                            <td className="p-3 text-sm text-gray-700 font-medium text-right">
                              {svc.total_requests}
                            </td>
                            <td className="p-3 text-sm text-green-600 font-medium text-right">
                              {svc.success_requests}
                            </td>
                            <td className="p-3 text-sm text-red-500 font-medium text-right">
                              {svc.failed_requests}
                            </td>
                            <td className="p-3 text-sm text-purple-600 font-medium text-right">
                              {svc.cache_requests}
                            </td>

                            {/* Vendor Request Breakdown */}
                            <td className="p-3 text-sm text-gray-600 text-right">
                              {svc.vendor_requests} /{" "}
                              <span className="text-green-600 font-medium">
                                {svc.vendor_success_requests}
                              </span>
                            </td>

                            {/* Health Trend Badge (UP / DOWN / STABLE) */}
                            <td className="p-3 text-center">
                              {svc.health_trend === "up" && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200 shadow-sm">
                                  <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                                  {svc.health_change_percentage > 0
                                    ? `+${svc.health_change_percentage}%`
                                    : `${svc.health_change_percentage}%`}
                                </span>
                              )}

                              {svc.health_trend === "down" && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 shadow-sm">
                                  <TrendingDown className="w-3.5 h-3.5 text-red-600" />
                                  {svc.health_change_percentage}%
                                </span>
                              )}

                              {(svc.health_trend === "stable" ||
                                !svc.health_trend) && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                                  <Minus className="w-3.5 h-3.5 text-gray-500" />
                                  {svc.health_change_percentage === 0
                                    ? "Stable (0%)"
                                    : `${svc.health_change_percentage}%`}
                                </span>
                              )}
                            </td>

                            {/* Success Percentage Badge */}
                            <td className="p-3 text-sm text-gray-600 text-right">
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                  svc.success_percentage >= 80
                                    ? "bg-green-100 text-green-700"
                                    : svc.success_percentage >= 50
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-red-100 text-red-700"
                                }`}
                              >
                                {svc.success_percentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* --- GLOBAL SERVICES TABLE (WITH PAGINATION) --- */}
          {responseData.services?.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Overall Services Data
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-800 text-white text-xs uppercase tracking-wider">
                      <th className="p-4 font-semibold rounded-tl-lg">
                        Service Info
                      </th>
                      <th className="p-4 font-semibold">Endpoint</th>
                      <th className="p-4 font-semibold">Vendor</th>
                      <th className="p-4 font-semibold">Monthly Volume</th>
                      <th className="p-4 font-semibold text-right">
                        Requests (Tot / Suc / Fail / Cache)
                      </th>
                      <th className="p-4 font-semibold text-right">
                        Vendor (Req / Suc)
                      </th>
                      <th className="p-4 font-semibold text-right">
                        Amount (₹)
                      </th>
                      <th className="p-4 font-semibold text-right">GST (₹)</th>
                      <th className="p-4 font-semibold text-center">
                        Health Trend
                      </th>
                      <th className="p-4 font-semibold text-right rounded-tr-lg">
                        Success %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentTableData.map((service, idx) => (
                      <tr
                        key={service.service_id || idx}
                        className="hover:bg-gray-50 transition"
                      >
                        {/* Service Info: Name, Code & ID */}
                        <td className="p-4">
                          <p className="text-sm font-bold text-gray-800">
                            {service.service_name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Code:{" "}
                            <span className="font-mono">
                              {service.service_code}
                            </span>{" "}
                            | ID: {service.service_id}
                          </p>
                        </td>

                        {/* Endpoint */}
                        <td className="p-4">
                          <code className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200 font-mono">
                            {service.end_point}
                          </code>
                        </td>

                        {/* Vendor Name */}
                        <td className="p-4 text-sm text-gray-700 font-medium">
                          {service.vendor_name}
                        </td>
                        {/* Vendor Name */}
                        <td className="p-4 text-sm text-gray-700 font-medium">
                          {service.volume_count}
                        </td>

                        {/* Requests Breakdown: Total / Success / Failed / Cache */}
                        <td className="p-4 text-right">
                          <div className="text-sm">
                            <span className="font-bold">
                              {service.total_requests}
                            </span>{" "}
                            /
                            <span className="text-green-600 font-medium mx-1">
                              {service.success_requests}
                            </span>{" "}
                            /
                            <span className="text-red-500 font-medium">
                              {service.failed_requests}
                            </span>{" "}
                            /
                            <span className="text-purple-600 font-medium ml-1">
                              {service.cache_requests}
                            </span>
                          </div>
                        </td>

                        {/* Vendor Requests / Vendor Success */}
                        <td className="p-4 text-right text-sm text-gray-600">
                          {service.vendor_requests} /{" "}
                          <span className="text-green-600 font-medium">
                            {service.vendor_success_requests}
                          </span>
                        </td>

                        {/* Total Amount */}
                        <td className="p-4 text-right text-sm text-gray-700 font-medium">
                          ₹{service.total_amount?.toFixed(2)}
                        </td>

                        {/* Total GST */}
                        <td className="p-4 text-right text-sm text-gray-500 font-medium">
                          ₹{service.total_gst?.toFixed(2)}
                        </td>

                        {/* Health Trend Badge */}
                        <td className="p-4 text-center">
                          {service.health_trend === "up" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200 shadow-sm">
                              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                              {service.health_change_percentage > 0
                                ? `+${service.health_change_percentage}%`
                                : `${service.health_change_percentage}%`}
                            </span>
                          )}

                          {service.health_trend === "down" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 shadow-sm">
                              <TrendingDown className="w-3.5 h-3.5 text-red-600" />
                              {service.health_change_percentage}%
                            </span>
                          )}

                          {(service.health_trend === "stable" ||
                            !service.health_trend) && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                              <Minus className="w-3.5 h-3.5 text-gray-500" />
                              {service.health_change_percentage === 0
                                ? "Stable (0%)"
                                : `${service.health_change_percentage || 0}%`}
                            </span>
                          )}
                        </td>

                        {/* Success Percentage Badge */}
                        <td className="p-4 text-right">
                          <span
                            className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold ${
                              service.success_percentage >= 80
                                ? "bg-green-100 text-green-700"
                                : service.success_percentage >= 50
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700"
                            }`}
                          >
                            {service.success_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 border-t pt-4">
                  <span className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold">
                      {Math.min(
                        currentPage * itemsPerPage,
                        totalGlobalServices,
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">{totalGlobalServices}</span>{" "}
                    entries
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="p-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-medium text-gray-700 px-4">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="p-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ServiceVolumeHealth;
